/* global window */

import { Controller } from '@hotwired/stimulus';
import { useClickOutside } from "stimulus-use";

// https://github.com/github/combobox-nav
import Combobox from "@github/combobox-nav";

// Targets
//
// * suggestionItem: A template tag with the HTML for each item in the combobox.
//
// * input: The input field that will be used for the autocomplete.
//
// * postalCode: The value of this element will be set to the postal code of the
//   place selected in the autocomplete dropdown.
//
// Values
//
// * googleKey: The Google Maps API key to use for the autocomplete. This is
//   passed to Google Maps. It must have the following APIs enabled: "Geocoding
//   API", "Maps JavaScript API", "Places API (New)".
//
// * language: The language to use for the autocomplete. This is passed to
//   Google Maps. See
//   https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest.language
//   for details.
//
// * region: Region code to use for formatting, ranking and maybe results
//   filtering. This is passed to Google Maps. See
//   https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest.region
//   for details.
export default class extends Controller {
  static targets = [
    "suggestionItem",
    "input",
    "list",
    "postalCode",
  ];

  static values = {
    googleKey: String,
    language: String,
    region: String,
  }

  clearPostalCodeTarget() {
    if (!this.hasPostalCodeTarget) { return }

    this.postalCodeTarget.value = "";
  }

  connect () {
    useClickOutside(this);

    this.createCombobox();
    this.setup();
  }

  clickOutside(event) {
    if (this.listTarget.hidden) return;

    this.hide();
  }

  createCombobox() {
    this.combobox = new Combobox(
      this.inputTarget,
      this.listTarget,
      {
        firstOptionSelectionMode: "active"
      }
    )
    this.listTarget.addEventListener('combobox-commit', this.elementSelected.bind(this))
  }

  disconnect() {
    if (this.combobox) { this.combobox.destroy() };
  }

  async elementSelected(event) {
    const selectedElement = event.target;

    const displayName = selectedElement.dataset.displayName;
    this.inputTarget.value = displayName;

    this.hide()

    // https://developers.google.com/maps/documentation/javascript/reference/place
    const place = new this.Place({
      id: selectedElement.id,
      requestedLanguage: this.languageValue,
      requestedRegion: this.regionValue
    });

    // https://developers.google.com/maps/documentation/javascript/reference/place#Place.fetchFields
    // https://developers.google.com/maps/documentation/javascript/reference/place#FetchFieldsRequest
    await place.fetchFields({
      fields: [
        "addressComponents",
        "location"
      ]
    })
    this.clearSession()

    if (place.addressComponents) {
      this.updateTargets(place);
    }
  }

  // Create and returns a new session token if one does not exist for the
  // current session.
  ensureSession() {
    if (!this.sessionToken) {
      return this.startSession()
    }

    return this.sessionToken;
  }

  // Returns the postal code for a Place. If the Place has address components
  // with a postal code that is returned. If not, we use reverse geocoding to
  // get the postal code for the location of the Place.
  async fetchPostalCodeFromPlace(place) {
    if (!place) { return }

    let postalCode = this.findPostalCode(place.addressComponents)
    if (postalCode) { return postalCode }

    const addressComponents = await this.getAddressComponentsFromLocation(place)
    postalCode = this.findPostalCode(addressComponents)
    return postalCode
  }

  async fetchSuggestions(event) {
    if (!this.googleMapsIsLoaded) { return; }

    if (!this.inputTarget.value) {
      this.hide();
      return;
    }

    // https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest
    let request = {
      input: this.inputTarget.value,
      language: this.languageValue,
      region: this.regionValue,
      sessionToken: this.ensureSession()
    };
    const { suggestions } = await this.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
    this.listTarget.innerHTML = "";

    if (suggestions && suggestions.length > 0) {

      suggestions.forEach((suggestion) => {
        // https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#PlacePrediction
        const placePrediction = suggestion.placePrediction;

        let content = this.suggestionItemTarget.innerHTML
        content = content.replace(/DISPLAY_NAME/g, placePrediction.text)
        content = content.replace(/MAIN_TEXT/g, placePrediction.mainText)
        content = content.replace(/PLACE_ID/g, placePrediction.placeId)
        content = content.replace(/SECONDARY_TEXT/g, placePrediction.secondaryText || "")
        this.listTarget.insertAdjacentHTML('beforeend', content);
      });

      this.show()
    } else {
      this.hide()
    }
  }

  // Returns the postal code from addressComponents.
  findPostalCode(addressComponents) {
    return addressComponents.find(component => component.types.includes("postal_code"));
  }

  async getAddressComponentsFromLocation(place) {
    // TODO: Should we preload this? Ensure it's only loaded once?
    const { Geocoder } = await google.maps.importLibrary("geocoding");
    const geocoder = new Geocoder();
    const response = await geocoder.geocode({location: place.location});

    // Get the postal code from the result
    const firstResult = response.results[0];
    if (firstResult) {
      return firstResult.address_components;
    } else {
      return [];
    }
  }

  hide() {
    // With inspiration from https://hybrd.co/posts/stimulus-autocomplete-with-combobox-navigation
    this.listTarget.hidden = true;
    this.combobox.clearSelection();
    this.combobox.stop();
  }

  async loadAutoComplete() {
    if (this.googleMapsIsLoaded) { return }

    const googleKey = this.googleKeyValue;

    // Google Maps Javascript API has it's own guarantee that it loads only
    // once.
    (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=`https://maps.${c}apis.com/maps/api/js?`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
      key: googleKey,
      v: "weekly",
    });

    const { AutocompleteSessionToken, AutocompleteSuggestion, Place} = await google.maps.importLibrary("places");
    this.googleMapsIsLoaded = true;
    this.AutocompleteSessionToken = AutocompleteSessionToken;
    this.AutocompleteSuggestion = AutocompleteSuggestion;
    this.Place = Place;
  }

  setup () {
    // Load the Google Maps API when we need it
    this.inputTarget.addEventListener("focus", this.loadAutoComplete.bind(this));
    this.inputTarget.addEventListener("input", this.loadAutoComplete.bind(this));
    this.inputTarget.addEventListener("mouseover", this.loadAutoComplete.bind(this));
  }

  show() {
    // With inspiration from https://hybrd.co/posts/stimulus-autocomplete-with-combobox-navigation
    this.listTarget.hidden = false;
    this.listTarget.style.display = "block";
    this.combobox.start();
  }

  clearSession() {
    this.sessionToken = null;
  }

  startSession() {
    if (!this.googleMapsIsLoaded) { return }

    this.sessionToken = new this.AutocompleteSessionToken();
  }

  updatePostalCodeTarget(postalCodeFromAddressComponents) {
    if (!this.hasPostalCodeTarget) { return }

    if (postalCodeFromAddressComponents) {
      const postalCodeText = postalCodeFromAddressComponents.longText || postalCodeFromAddressComponents.long_name;
      this.postalCodeTarget.value = postalCodeText;
    }
    else {
      this.clearPostalCodeTarget()
    }
  }

  async updateTargets (place) {
    if (!this.hasPostalCodeTarget) { return }

    let postalCode = await this.fetchPostalCodeFromPlace(place)
    this.updatePostalCodeTarget(postalCode);
  }
}
